import { OnlineOrderBranchSettingFragment } from '@client/graphql/online-order-branch-setting-fragment.ts';
import { OnlineOrderFragment, OnlineOrderProductsFragment } from '@client/graphql/online-order-fragment';
import { getFragmentData } from '@client/graphql/types';
import {
  OnlineOrderDeliveryMode,
  OnlineOrderItemFragment,
  OnlineOrderPaymentMethod,
} from '@client/graphql/types/graphql';
import ApplicationErrorView from '@client/module/error/application-error-view.tsx';
import OnlineOrderAction from '@client/page/management/online-order/detail/component/online-order-action.tsx';
import OnlineOrderManualTransferPanel from '@client/page/management/online-order/detail/component/online-order-manual-transfer-panel.tsx';
import OnlineProductTable from '@client/page/management/online-order/detail/component/online-product-table.tsx';
import usePrintToKitchen from '@client/page/management/online-order/detail/logic/use-print-to-kitchen.ts';
import { MapPreview } from '@client/page/management/online-order/detail/map-preview.tsx';
import { useFocusedBranchContext } from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import { PrinterIcon } from '@heroicons/react/24/outline';
import CardTableContent from '@packages/ui/card/card-content-table.tsx';
import DescriptionSection from '@packages/ui/description/description-section.tsx';
import { useTranslate } from '@tolgee/react';
import { capitalize } from 'lodash';
import { useParams } from 'react-router-dom';
import Card from 'ui/src/card/card';
import CardContent from 'ui/src/card/card-content';
import CardHeader from 'ui/src/card/card-header';
import { ContentLayout, PrimaryContent } from 'ui/src/content-layout';
import DescriptionItem from 'ui/src/description/description-item';
import FormLayout from 'ui/src/form/form-layout';
import Loading from 'ui/src/loading';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context.ts';
import CurrencyView from '@packages/core/utils/currency-view';
import moment from 'moment-timezone';
import useGetOnlineOrder from './logic/use-get-online-order';

export default function OnlineOrderDetail() {
  // Params
  const onlineOrderID = useParams().onlineOrderID ?? '';
  // const previousPath = useContext(CustomLocationContext) ?? '';

  const branchProvider = useFocusedBranchContext();
  const companyProvider = useFocusedCompanyContext();

  const branchID = branchProvider.branch?.id ?? '';

  const { data, loading, error } = useGetOnlineOrder(onlineOrderID, branchID);

  const [printToKitchen] = usePrintToKitchen();

  const onlineOrderData = getFragmentData(OnlineOrderFragment, data?.onlineOrder);
  const onlineOrderProducts = getFragmentData(OnlineOrderProductsFragment, onlineOrderData?.onlineOrderProducts);
  const configuration = getFragmentData(OnlineOrderBranchSettingFragment, data?.onlineOrderBranchConfiguration);
  const { t } = useTranslate();
  if (loading) {
    return <Loading />;
  }

  if (!onlineOrderData || !onlineOrderProducts || !configuration) {
    return <>No data</>;
  }

  return (
    <>
      <ContentLayout>
        <PrimaryContent>
          <ApplicationErrorView error={error} />
          <OnlineOrderAction order={onlineOrderData} compact={true} />
          {onlineOrderData.paymentMethod === OnlineOrderPaymentMethod.ManualBankTransfer && (
            <OnlineOrderManualTransferPanel order={onlineOrderData} timezone={companyProvider.settings.timezone} />
          )}
          <Card>
            <CardHeader title={t('online-order.detail.order.title')} withBackButton={true} backTarget={-1} />
            <CardTableContent>
              <OnlineProductTable order={onlineOrderData} configuration={configuration} />
            </CardTableContent>
          </Card>
          <Card>
            <CardHeader title={t('online-order.detail.title')}>
              <Menu>
                <MenuButton
                  className="flex items-center gap-2 shadow-xs rounded-md h-8 px-3 font-semibold shadow-white/10
          focus:outline-none text-xl"
                >
                  <EllipsisHorizontalIcon className="w-6 h-6 text-gray-700" />
                </MenuButton>

                <MenuItems
                  transition
                  anchor="bottom start"
                  className="border border-gray-200 rounded-xl bg-white shadow-md [--anchor-gap:8px] [--anchor-padding:8px]"
                >
                  <MenuItem>
                    <button
                      onClick={() => {
                        void printToKitchen({ variables: { id: onlineOrderID } });
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-3 hover:bg-gray-100"
                    >
                      <PrinterIcon className="w-6 h-6" />
                      Print to kitchen
                      <kbd className="ml-auto hidden font-sans text-xs text-white/50 group-data-[focus]:inline">⌘E</kbd>
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </CardHeader>
            <CardContent>
              <FormLayout>
                <DescriptionItem title={t('common.form-input.ID')}>{onlineOrderData.shortID}</DescriptionItem>

                <DescriptionItem title={t('common.form-input.status')}>
                  {capitalize(onlineOrderData.state.replace(/_/g, ' '))}
                </DescriptionItem>
                <div className="grid md:grid-cols-2 gap-y-4">
                  <DescriptionItem title={t('common.form-input.create-time', 'Created time')}>
                    {moment(onlineOrderData.userCreatedAt || onlineOrderData.createdAt)
                      .tz(companyProvider.settings.timezone)
                      .format('HH:mm')}
                  </DescriptionItem>

                  {onlineOrderData.expectedTime && (
                    <DescriptionItem title={t('online-order.dashboard.order-status.dialog.order-time', 'Order time')}>
                      {moment(onlineOrderData.expectedTime).tz(companyProvider.settings.timezone).format('HH:mm')}
                    </DescriptionItem>
                  )}
                </div>

                <div className="grid md:grid-cols-2">
                  <DescriptionItem title={t('common.form-input.guest-name')}>
                    {onlineOrderData.guestName}
                  </DescriptionItem>

                  <DescriptionItem
                    title={t('common.form-input.email')}
                    action={[
                      {
                        href: `mailto:${onlineOrderData.email ?? ''}`,
                        name: t('online-order.detail.email.action.send-email', 'Send Email'),
                      },
                    ]}
                  >
                    {onlineOrderData.email}
                  </DescriptionItem>

                  <DescriptionItem
                    title={t('common.form-input.phone')}
                    action={[
                      {
                        href: `sms:${onlineOrderData.phone ?? ''}`,
                        name: t('online-order.detail.phone.action.send-sms', 'Send SMS'),
                      },
                      {
                        href: `tel:${onlineOrderData.phone ?? ''}`,
                        name: t('online-order.detail.phone.action.call', 'Call now'),
                      },
                    ]}
                  >
                    {onlineOrderData.phone}
                  </DescriptionItem>

                  {(onlineOrderData.message?.length ?? 0) > 0 && (
                    <DescriptionItem title={t('common.form-input.message')}>{onlineOrderData.message}</DescriptionItem>
                  )}
                </div>

                <DescriptionSection title={t('online-order.detail.delivery.title')}>
                  <DescriptionItem title={t('online-order.detail.delivery.information')}>
                    {t('online-order.detail.delivery.type.' + capitalize(onlineOrderData.deliveryMode) + '.title')}
                  </DescriptionItem>

                  {onlineOrderData.deliveryMode == OnlineOrderDeliveryMode.Delivery && (
                    <>
                      <DescriptionItem title={t('online-order.detail.delivery.type.Delivery.location')}>
                        {formatAddress(onlineOrderData)}
                      </DescriptionItem>
                      <DescriptionItem title={t('online-order.detail.delivery.type.Delivery.distance')}>
                        {((onlineOrderData.distanceToRestaurant ?? 0) / 1000).toFixed(2)} kmm
                      </DescriptionItem>
                    </>
                  )}

                  {onlineOrderData.latitude && onlineOrderData.longitude && (
                    <MapPreview latitude={onlineOrderData.latitude} longitude={onlineOrderData.longitude} />
                  )}
                </DescriptionSection>

                <DescriptionSection title={t('online-order.detail.payment.title')}>
                  <DescriptionItem title={t('online-order.detail.payment.method')}>
                    {(() => {
                      switch (onlineOrderData.paymentMethod) {
                        case OnlineOrderPaymentMethod.Cash:
                          return t('online-order.detail.payment.method-value.cash', 'Cash');
                        case OnlineOrderPaymentMethod.CardAtPlace:
                          return t('online-order.detail.payment.method-value.card-at-place', 'Card on site');
                        case OnlineOrderPaymentMethod.Stripe:
                          return t('online-order.detail.payment.method-value.stripe', 'Card');
                        case OnlineOrderPaymentMethod.ManualBankTransfer:
                          return t('online-order.detail.payment.method-value.online-payment', 'Online Payment');
                        default:
                          return '-';
                      }
                    })()}
                  </DescriptionItem>

                  {onlineOrderData.invoice?.stripeCheckoutSession && (
                    <>
                      {onlineOrderData.invoice.stripeCheckoutSession.refund == undefined && (
                        <>
                          <DescriptionItem title={t('online-order.detail.payment.status')}>
                            {capitalize(onlineOrderData.invoice.stripeCheckoutSession.paymentStatus)}
                          </DescriptionItem>

                          {onlineOrderData.invoice.stripeCheckoutSession.paymentIntentId && (
                            <DescriptionItem title={t('online-order.detail.payment.dashboard')}>
                              <a
                                href={`https://dashboard.stripe.com/payments/${onlineOrderData.invoice.stripeCheckoutSession.paymentIntentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {t('online-order.detail.payment.view-in-dashboard', 'View in Stripe Dashboard')}
                              </a>
                            </DescriptionItem>
                          )}
                        </>
                      )}

                      {onlineOrderData.invoice.stripeCheckoutSession.refund && (
                        <DescriptionItem title={t('online-order.detail.payment.refund')}>
                          <div>
                            <div>
                              {onlineOrderData.invoice.stripeCheckoutSession.refund.status
                                ? capitalize(onlineOrderData.invoice.stripeCheckoutSession.refund.status)
                                : '-'}
                            </div>
                            {onlineOrderData.invoice.stripeCheckoutSession.refund.reason && (
                              <div className="text-sm text-gray-500">
                                {onlineOrderData.invoice.stripeCheckoutSession.refund.reason}
                              </div>
                            )}
                            <div className="text-sm text-gray-500">
                              <CurrencyView
                                price={onlineOrderData.invoice.stripeCheckoutSession.refund.amount}
                                currency={onlineOrderData.invoice.stripeCheckoutSession.refund.currency.toUpperCase()}
                              />
                            </div>
                          </div>

                          {onlineOrderData.invoice.stripeCheckoutSession.refund.paymentIntentId && (
                            <DescriptionItem title={t('online-order.detail.payment.refund-dashboard')}>
                              <a
                                href={`https://dashboard.stripe.com/payments/${onlineOrderData.invoice.stripeCheckoutSession.refund.paymentIntentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {t('online-order.detail.payment.view-in-dashboard', 'View in Stripe Dashboard')}
                              </a>
                            </DescriptionItem>
                          )}
                        </DescriptionItem>
                      )}
                    </>
                  )}
                </DescriptionSection>
              </FormLayout>
            </CardContent>
          </Card>
        </PrimaryContent>
      </ContentLayout>
    </>
  );
}

const formatAddress = (onlineOrder: OnlineOrderItemFragment) => {
  const components = [
    onlineOrder.streetAddress,
    onlineOrder.streetAddress2,
    onlineOrder.addressLocality,
    `${onlineOrder.postalCode ?? ''} ${onlineOrder.addressRegion ?? ''}`,
  ].filter((component) => (component?.length ?? 0) > 0);

  return components.join(', ');
};
